import {
  Alignment,
  Block,
  BlockAnimation,
  Order,
} from "interfaces/interface-studio-canvas";
import { create } from "zustand";
import { useStudioHistoryStore } from "./StudioHistoryStore";
// import { persist } from 'zustand/middleware';
import { useMoveableStore } from "./MoveableStore";
interface SlideHistoryEntry {
  history: Block[][];
  historyIndex: number;
  initialBlocks: Block[];
}

interface UseCanvasStoreInterface {
  blocks: Block[];
  activeBlockId: string | null;
  clipboard: Block | null;
  canvasBackgroundColor: string;
  canvasWidth: number;
  canvasHeight: number;
  isDragging: boolean;
  focusedBlockId: string | null;
  canvasScale: number;

  /** Post-action snapshots for the current slide (push-after design). */
  _history: Block[][];
  /** -1 = at initial state; N = at _history[N] */
  _historyIndex: number;
  /** Blocks state when this slide was first loaded (undo target from index 0). */
  _initialBlocks: Block[];
  /** Slide ID that is currently loaded. */
  _currentSlideId: string | null;
  /** Per-slide history, keyed by slideId, preserved across navigation. */
  _slideHistories: Record<string, SlideHistoryEntry>;

  /**
   * Navigation callback registered by studio-edit.
   * Called when cross-slide undo/redo needs to switch to the affected slide.
   * The callback receives the slideId (format: `${documentId}_${slideIndex}`)
   * and must synchronously call setBlocks to load the target slide's data.
   */
  _navigateToSlide: ((slideId: string) => void) | null;
  setNavigateToSlide: (cb: ((slideId: string) => void) | null) => void;

  pushToHistory: () => void;
  undo: () => void;
  redo: () => void;

  // History introspection helpers for global Studio history
  getHistoryIndex: () => number;
  getHistoryLength: () => number;

  /** slideId keeps per-slide history intact across navigation. Omit for same-slide updates. */
  setBlocks: (blocks: Block[], slideId?: string) => void;
  setActiveBlock: (id: string | null) => void;
  updateBlock: (id: string, updates: Partial<Block>) => void;
  createNewBlock: (block: Omit<Block, "zIndex">) => void;
  deleteBlock: (id: string) => void;
  alignBlock: (alignment: Alignment) => void;
  reorderBlock: (id: string, order: Order) => void;
  deSelectBlocks: () => void;

  copyBlock: () => void;
  cutBlock: () => void;
  pasteBlock: () => void;
  duplicateBlock: () => void;

  setCanvasBackgroundColor: (color: string, skipHistory?: boolean) => void;
  setCanvasSize: (w: number, h: number) => void;
  resetZIndex: () => void;

  setIsDragging: (isDragging: boolean) => void;
  setFocusedBlockId: (id: string | null) => void;
  setCanvasScale: (scale: number) => void;

  //
  copyBlockWithSystem: (useSystemClipboard?: boolean) => Promise<boolean>;
  cutBlockWithSystem: (useSystemClipboard?: boolean) => Promise<boolean>;
  pasteBlockFromSystem: (useSystemClipboard?: boolean) => Promise<boolean>;
  importBlockFromClipboard: (useSystemClipboard?: boolean) => Promise<boolean>;
  // copyBlockWithSystem: (useSystemClipboard?:boolean) => void;

  removeDefaultParagraphIfNeeded: (blocks: Block[]) => Block[];

  // Animation actions
  addAnimation: (
    blockId: string,
    animation: Omit<BlockAnimation, "id" | "order">,
  ) => void;
  updateAnimation: (
    blockId: string,
    animationId: string,
    updates: Partial<BlockAnimation>,
  ) => void;
  removeAnimation: (blockId: string, animationId: string) => void;
}

export const useCanvasStore = create<UseCanvasStoreInterface>()(
  // persist(
  (set, get) => ({
    blocks: [],
    activeBlockId: null,
    clipboard: null,
    canvasBackgroundColor: "#FEFFF9",
    canvasWidth: 800,
    canvasHeight: 450,
    isDragging: false,
    focusedBlockId: null,
    canvasScale: 1,
    _history: [],
    _historyIndex: -1,
    _initialBlocks: [],
    _currentSlideId: null,
    _slideHistories: {},
    _navigateToSlide: null,
    setNavigateToSlide: (cb) => set({ _navigateToSlide: cb }),

    setBlocks: (blocks, slideId) => {
      const {
        _currentSlideId,
        _history,
        _historyIndex,
        _initialBlocks,
        _slideHistories,
      } = get();

      // Same slide or no slideId: just refresh the visible blocks without touching history.
      if (slideId === undefined || slideId === _currentSlideId) {
        set({ blocks });
        return;
      }

      // Save the current slide's full history before leaving it
      const updatedHistories: Record<string, SlideHistoryEntry> = {
        ..._slideHistories,
      };
      if (_currentSlideId != null) {
        updatedHistories[_currentSlideId] = {
          history: _history,
          historyIndex: _historyIndex,
          initialBlocks: _initialBlocks,
        };
      }

      // Restore the target slide's history, or start fresh for a first visit
      const saved = updatedHistories[slideId];
      set({
        blocks,
        _history: saved?.history ?? [],
        _historyIndex: saved?.historyIndex ?? -1,
        _initialBlocks: saved?.initialBlocks ?? blocks,
        _currentSlideId: slideId,
        _slideHistories: updatedHistories,
      });
    },
    setActiveBlock: (id) => set({ activeBlockId: id }),

    updateBlock: (id, updates) =>
      set((state) => ({
        blocks: state.blocks.map((block) => {
          // console.log('Update block ran!!!');
          if (block.id !== id) return block;

          const newBlock = { ...block, ...updates };

          // Only modify the transform if it's being updated
          if (updates.transform) {
            newBlock.transform = {
              ...block.transform,
              ...updates.transform,
            };
          }

          return newBlock;
        }),
      })),

    createNewBlock: (block) => {
      const highestZ = Math.max(10, ...get().blocks.map((b) => b.zIndex ?? 0));
      const newBlock: Block = {
        ...block,
        zIndex: highestZ + 1,
      } as Block;

      set((state) => {
        const updatedBlocks = [...state.blocks, newBlock];
        const cleanedBlocks =
          get().removeDefaultParagraphIfNeeded(updatedBlocks);

        return {
          blocks: cleanedBlocks,
          activeBlockId: newBlock.id,
        };
      });
      // Push AFTER mutation so history captures the new state
      get().pushToHistory();
    },

    // Helper function to add to your store
    removeDefaultParagraphIfNeeded: (blocks) => {
      // Check if there are any non-default blocks
      const nonDefaultBlocks = blocks.filter((b) => !b.isDefaultBlock);

      // If there are non-default blocks, remove unused default paragraphs
      if (nonDefaultBlocks.length > 0) {
        return blocks.filter((b) => {
          // Remove default paragraph blocks that haven't been modified
          if (
            b.isDefaultBlock &&
            b.type === "text" &&
            "htmlContent" in b &&
            (b.htmlContent === "<p></p>" || !b.htmlContent?.trim())
          ) {
            return false;
          }
          return true;
        });
      }

      return blocks;
    },

    deleteBlock: (id) => {
      set((state) => ({
        blocks: state.blocks.filter((b) => b.id !== id),
        activeBlockId: state.activeBlockId === id ? null : state.activeBlockId,
      }));
      get().pushToHistory();
    },

    alignBlock: (position) => {
      const { blocks, activeBlockId, canvasWidth, canvasHeight } = get();
      const blk = blocks.find((b) => b.id === activeBlockId);
      if (!blk) return;

      const { translate } = blk.transform;
      let x = translate.x;
      let y = translate.y;

      switch (position) {
        case "left":
          x = 0;
          break;
        case "right":
          x = canvasWidth - blk.width;
          break;
        case "top":
          y = 0;
          break;
        case "bottom":
          y = canvasHeight - blk.height;
          break;
        case "horizontal-centered":
          x = (canvasWidth - blk.width) / 2;
          break;
        case "vertical-centered":
          y = (canvasHeight - blk.height) / 2;
          break;
      }

      set({
        blocks: blocks.map((b) =>
          b.id === blk.id
            ? { ...b, transform: { ...b.transform, translate: { x, y } } }
            : b,
        ),
      });
      get().pushToHistory();

      requestAnimationFrame(() => {
        if (blk.id) {
          useMoveableStore.getState().updateMoveableRect();
        }
      });
    },

    reorderBlock: (id, direction) => {
      const blocks = [...get().blocks];
      const blk = blocks.find((b) => b.id === id);
      if (!blk) return;

      const MIN_Z = 10;
      const sorted = [...blocks].sort(
        (a, b) => (a.zIndex ?? MIN_Z) - (b.zIndex ?? MIN_Z),
      );
      const zList = sorted.map((b) => b.zIndex ?? MIN_Z);
      let targetZ = blk.zIndex ?? MIN_Z;

      if (direction === "front") targetZ = Math.max(...zList) + 1;
      else if (direction === "back") targetZ = MIN_Z;
      else if (direction === "forward") targetZ += 1;
      else if (direction === "backward") targetZ -= 1;

      if (targetZ < MIN_Z) targetZ = MIN_Z;

      set({
        blocks: blocks.map((b) => {
          if (b.id === blk.id) return { ...b, zIndex: targetZ };
          if (b.zIndex !== undefined && b.zIndex >= targetZ)
            return { ...b, zIndex: b.zIndex + 1 };
          return b;
        }),
      });
      get().pushToHistory();
    },

    deSelectBlocks: () => {
      // console.log('Deselect blocks ran!');
      // console.trace();
      useMoveableStore.setState({ targets: [] });
      set({ activeBlockId: null });
    },

    copyBlock: () => {
      const { activeBlockId, blocks } = get();
      set({ clipboard: blocks.find((b) => b.id === activeBlockId) ?? null });
    },

    cutBlock: () => {
      const { activeBlockId, blocks } = get();
      const block = blocks.find((b) => b.id === activeBlockId);
      if (!block) return;
      set({
        clipboard: block,
        blocks: blocks.filter((b) => b.id !== block.id),
        activeBlockId: null,
      });
      get().pushToHistory();
    },

    pasteBlock: () => {
      const clip = get().clipboard;
      if (!clip) return;

      const highestZ = Math.max(10, ...get().blocks.map((b) => b.zIndex ?? 0));
      const newBlock: Block = {
        ...clip,
        id: crypto.randomUUID(),
        zIndex: highestZ + 1,
        transform: {
          ...clip.transform,
          translate: {
            x: clip.transform.translate.x + 10,
            y: clip.transform.translate.y + 10,
          },
        },
      };
      set((state) => ({
        blocks: [...state.blocks, newBlock],
        activeBlockId: newBlock.id,
      }));
      get().pushToHistory();
    },

    duplicateBlock: () => {
      const { activeBlockId, blocks } = get();
      const block = blocks.find((b) => b.id === activeBlockId);
      if (!block) return;

      const newBlock: Block = {
        ...structuredClone(block),
        id: crypto.randomUUID(),
        zIndex: Math.max(...blocks.map((b) => b.zIndex ?? 0)) + 1,
        transform: {
          ...block.transform,
          translate: {
            x: block.transform.translate.x + 20,
            y: block.transform.translate.y + 20,
          },
        },
      };
      set({ blocks: [...blocks, newBlock], activeBlockId: newBlock.id });
      get().pushToHistory();
    },

    setCanvasBackgroundColor: (color, skipHistory?: boolean) => {
      if (skipHistory) {
        set({ canvasBackgroundColor: color });
        return;
      }
      const prevColor = get().canvasBackgroundColor;
      if (prevColor === color) return;
      set({ canvasBackgroundColor: color });

      // Register in global history
      try {
        const pushGlobal = useStudioHistoryStore.getState().push;
        pushGlobal({
          id: crypto.randomUUID(),
          timestamp: Date.now(),
          label: "Background color",
          type: "canvas",
          scope: { slideId: get()._currentSlideId ?? null },
          meta: { prevColor, newColor: color },
          undo: () => {
            set({ canvasBackgroundColor: prevColor });
          },
          redo: () => {
            set({ canvasBackgroundColor: color });
          },
        });
      } catch {
        // best-effort
      }
    },
    setCanvasSize: (w, h) => set({ canvasWidth: w, canvasHeight: h }),

    resetZIndex: () =>
      set((state) => ({
        blocks: state.blocks.map((b, i) => ({ ...b, zIndex: i + 10 })),
      })),

    setIsDragging: (isDragging) => set({ isDragging }),
    setFocusedBlockId: (id) => set({ focusedBlockId: id }),
    setCanvasScale: (scale) => set({ canvasScale: scale }),

    /**
     * Push-after design: call pushToHistory AFTER each mutation.
     * _history[i] = the blocks state after action i.
     * _historyIndex = N means we are currently at post-action-N state.
     * _historyIndex = -1 means we are at the initial (pre-any-action) state.
     */
    pushToHistory: () => {
      const { blocks, _history, _historyIndex } = get();
      // Truncate any redo states (branching from current position)
      const newHistory = _history.slice(0, _historyIndex + 1);
      newHistory.push(structuredClone(blocks));
      // Cap at 50 undo steps
      if (newHistory.length > 50) newHistory.shift();
      set({ _history: newHistory, _historyIndex: newHistory.length - 1 });

      // Also register a global Studio history entry for this canvas change
      try {
        const capturedSlideId = get()._currentSlideId;
        const historyIndex = newHistory.length - 1;

        const pushGlobal = useStudioHistoryStore.getState().push;
        pushGlobal({
          id: crypto.randomUUID(),
          timestamp: Date.now(),
          label: "Canvas change",
          type: "canvas",
          scope: { slideId: capturedSlideId ?? null },
          meta: { historyIndex },
          undo: () => {
            const currentSlideId = get()._currentSlideId;
            if (currentSlideId === capturedSlideId) {
              // Same slide — undo directly on the live canvas
              get().undo();
            } else if (capturedSlideId != null) {
              // Navigate to the target slide first (this synchronously loads
              // the slide's blocks + per-slide history via setBlocks), then undo.
              const navigate = get()._navigateToSlide;
              if (navigate) {
                navigate(capturedSlideId);
                get().undo();
              } else {
                // Fallback: adjust saved history without navigation
                const histories = { ...get()._slideHistories };
                const saved = histories[capturedSlideId];
                if (saved && saved.historyIndex >= 0) {
                  histories[capturedSlideId] = {
                    ...saved,
                    historyIndex: saved.historyIndex - 1,
                  };
                  set({ _slideHistories: histories });
                }
              }
            }
          },
          redo: () => {
            const currentSlideId = get()._currentSlideId;
            if (currentSlideId === capturedSlideId) {
              // Same slide — redo directly on the live canvas
              get().redo();
            } else if (capturedSlideId != null) {
              // Navigate to the target slide first, then redo.
              const navigate = get()._navigateToSlide;
              if (navigate) {
                navigate(capturedSlideId);
                get().redo();
              } else {
                // Fallback: adjust saved history without navigation
                const histories = { ...get()._slideHistories };
                const saved = histories[capturedSlideId];
                if (saved && saved.historyIndex < saved.history.length - 1) {
                  histories[capturedSlideId] = {
                    ...saved,
                    historyIndex: saved.historyIndex + 1,
                  };
                  set({ _slideHistories: histories });
                }
              }
            }
          },
        });
      } catch {
        // history is best-effort; ignore errors here
      }
    },

    undo: () => {
      const { _history, _historyIndex, _initialBlocks } = get();
      // Nothing to undo when already at the initial state
      if (_historyIndex < 0) return;
      const newIndex = _historyIndex - 1;
      // Going below 0 means restoring the slide's initial blocks
      const blocks =
        newIndex < 0
          ? structuredClone(_initialBlocks)
          : structuredClone(_history[newIndex]);
      set({ blocks, _historyIndex: newIndex });
    },

    redo: () => {
      const { _history, _historyIndex } = get();
      if (_historyIndex >= _history.length - 1) return;
      const newIndex = _historyIndex + 1;
      set({
        blocks: structuredClone(_history[newIndex]),
        _historyIndex: newIndex,
      });
    },

    getHistoryIndex: () => {
      return get()._historyIndex;
    },

    getHistoryLength: () => {
      return get()._history.length;
    },

    //
    // Add these methods to your existing useCanvasStore

    // Enhanced copy method that also copies to system clipboard
    copyBlockWithSystem: async (useSystemClipboard = true) => {
      const { activeBlockId, blocks } = get();
      const block = blocks.find((b) => b.id === activeBlockId);

      if (!block) return false;

      // Always update internal clipboard
      set({ clipboard: block });

      // Optionally copy to system clipboard
      if (useSystemClipboard && navigator.clipboard) {
        try {
          const clipboardData = {
            type: "canvas-block",
            version: "1.0",
            data: block,
            timestamp: Date.now(),
          };

          await navigator.clipboard.writeText(
            JSON.stringify(clipboardData, null, 2),
          );
          return true;
        } catch (error) {
          console.warn("Failed to copy to system clipboard:", error);
          return false;
        }
      }

      return true;
    },

    // Enhanced cut method that also copies to system clipboard
    cutBlockWithSystem: async (useSystemClipboard = true) => {
      const { activeBlockId, blocks } = get();
      const block = blocks.find((b) => b.id === activeBlockId);

      if (!block) return false;

      // Always update internal clipboard and remove block
      set({
        clipboard: block,
        blocks: blocks.filter((b) => b.id !== block.id),
        activeBlockId: null,
      });

      // Optionally copy to system clipboard
      if (useSystemClipboard && navigator.clipboard) {
        try {
          const clipboardData = {
            type: "canvas-block",
            version: "1.0",
            data: block,
            timestamp: Date.now(),
            operation: "cut",
          };

          await navigator.clipboard.writeText(
            JSON.stringify(clipboardData, null, 2),
          );
          return true;
        } catch (error) {
          console.warn("Failed to copy to system clipboard:", error);
          return false;
        }
      }

      return true;
    },

    // Enhanced paste method that can paste from system clipboard
    pasteBlockFromSystem: async (preferSystemClipboard = true) => {
      let blockToPaste: Block | null = null;

      // Try to get from system clipboard first
      if (preferSystemClipboard && navigator.clipboard) {
        try {
          const text = await navigator.clipboard.readText();
          const clipboardData = JSON.parse(text);

          if (
            clipboardData.type === "canvas-block" &&
            clipboardData.data &&
            clipboardData.data.id
          ) {
            blockToPaste = clipboardData.data as Block;
          }
        } catch (_e) {
          // system clipboard does not contain valid block data; fall back to internal clipboard
        }
      }

      // Fallback to internal clipboard
      if (!blockToPaste) {
        blockToPaste = get().clipboard;
      }

      if (!blockToPaste) return false;

      const highestZ = Math.max(10, ...get().blocks.map((b) => b.zIndex ?? 0));

      const newBlock: Block = {
        ...structuredClone(blockToPaste),
        id: crypto.randomUUID(),
        zIndex: highestZ + 1,
        transform: {
          ...blockToPaste.transform,
          translate: {
            x: blockToPaste.transform.translate.x + 10,
            y: blockToPaste.transform.translate.y + 10,
          },
        },
      };

      set((state) => ({
        blocks: [...state.blocks, newBlock],
        activeBlockId: newBlock.id,
      }));

      return true;
    },

    // Animation actions
    addAnimation: (blockId, animation) => {
      const { blocks } = get();
      // Compute next slide-global order
      let maxOrder = 0;
      for (const b of blocks) {
        if (b.animations) {
          for (const a of b.animations) {
            if (a.order > maxOrder) maxOrder = a.order;
          }
        }
      }
      const newAnimation: BlockAnimation = {
        ...animation,
        id: crypto.randomUUID(),
        order: maxOrder + 1,
      };
      set({
        blocks: blocks.map((b) =>
          b.id === blockId
            ? { ...b, animations: [...(b.animations ?? []), newAnimation] }
            : b,
        ),
      });
      get().pushToHistory();
    },

    updateAnimation: (blockId, animationId, updates) => {
      set((state) => ({
        blocks: state.blocks.map((b) =>
          b.id === blockId
            ? {
                ...b,
                animations: (b.animations ?? []).map((a) =>
                  a.id === animationId ? { ...a, ...updates } : a,
                ),
              }
            : b,
        ),
      }));
      get().pushToHistory();
    },

    removeAnimation: (blockId, animationId) => {
      const { blocks } = get();
      const block = blocks.find((b) => b.id === blockId);
      if (!block) return;
      const removedAnim = block.animations?.find((a) => a.id === animationId);
      if (!removedAnim) return;
      const removedOrder = removedAnim.order;

      // Remove the animation and re-sequence orders
      set({
        blocks: blocks.map((b) => ({
          ...b,
          animations: (b.animations ?? [])
            .filter((a) => !(b.id === blockId && a.id === animationId))
            .map((a) =>
              a.order > removedOrder ? { ...a, order: a.order - 1 } : a,
            ),
        })),
      });
      get().pushToHistory();
    },

    // Method to import blocks from external sources
    importBlockFromClipboard: async () => {
      if (!navigator.clipboard) return false;

      try {
        const text = await navigator.clipboard.readText();

        // Try different formats

        // 1. Try our canvas block format
        try {
          const clipboardData = JSON.parse(text);
          if (clipboardData.type === "canvas-block" && clipboardData.data) {
            const block = clipboardData.data as Block;
            get().pasteBlockFromSystem(false); // Use the block we just validated
            return true;
          }
        } catch (e) {
          // Not our format, try other formats
        }

        // 2. Try to create a text block from plain text
        if (text.length > 0 && text.length < 1000) {
          // Reasonable text length
          const newBlock: Omit<Block, "zIndex"> = {
            id: crypto.randomUUID(),
            type: "text",
            htmlContent: `<p>${text}</p>`,
            width: 200,
            height: 50,
            transform: {
              translate: {
                x: 0,
                y: 0,
              },
              rotate: 0,
              flipHorizontal: 1,
              flipVertical: 1,
            },
            // Add other required properties based on your Block interface
          } as Block;

          get().createNewBlock(newBlock);
          return true;
        }

        return false;
      } catch (error) {
        console.error("Failed to import from clipboard:", error);
        return false;
      }
    },
  }),
  // { name: 'canvas-block-store' }
  // )
);

// import {
//   Alignment,
//   Block,
//   CanvasPage,
//   Order,
// } from 'interfaces/interface-studio-canvas';
// import { create } from 'zustand';
// import { persist } from 'zustand/middleware';
// import { useMoveableStore } from './MoveableStore';

// interface UseCanvasStoreInterface {
//   activePageId: string | null;
//   activeBlockId: string | null;
//   pages: CanvasPage[];

//   setActivePage: (id: string) => void;
//   createNewPage: () => void;
//   deletePage: (id: string) => void;

//   setActiveBlock: (id: string | null) => void;
//   updateBlock: (id: string, updates: Partial<Block>) => void;
//   createNewBlock: (block: Omit<Block, 'zIndex'>) => Promise<void>;
//   deleteBlock: (id: string) => void;
//   alignBlock: (alignment: Alignment) => void;
//   reorderBlock: (id: string, order: Order) => void;
//   deSelectBlocks: () => void;

//   clipboard: Block | null;
//   copyBlock: () => void;
//   cutBlock: () => void;
//   pasteBlock: () => void;
//   duplicateBlock: () => void;

//   canvasBackgroundColor: string;
//   setCanvasBackgroundColor: (color: string) => void;
//   canvasWidth: number;
//   canvasHeight: number;
//   setCanvasSize: (w: number, h: number) => void;

//   // handling when a block is being dragged
//   isDragging: boolean;
//   setIsDragging: (isDragging: boolean) => void;

//   // Handling block item that's in focus
//   focusedBlockId: string | null;
//   setFocusedBlockId: (id: string | null) => void;

//   // addShape: (svgType: Shapes) => void;
//   // addTriangleBlock: () => void;

//   canvasScale: number;
//   setCanvasScale: (scale: number) => void;

//   //
//   resetPage: () => void;
// }

// export const useCanvasStore = create<UseCanvasStoreInterface>()(
//   persist(
//     (set, get) => {
//       // Initialize with one default page
//       const defaultPageId = `${crypto.randomUUID()}`;
//       const defaultPage: CanvasPage = {
//         id: defaultPageId,
//         name: 'Page 1',
//         blocks: [],
//         canvasBackgroundColor: '#FEFFF9',
//         canvasWidth: 800,
//         canvasHeight: 450,
//       };

//       // getting targets from moveableStore
//       // const setTargets = useMoveableStore((state) => state.setTargets);
//       return {
//         // State
//         activePageId: defaultPageId,
//         activeBlockId: null,
//         pages: [defaultPage],
//         clipboard: null,
//         canvasBackgroundColor: '#FEFFF9',
//         canvasWidth: 800,
//         canvasHeight: 450,
//         isDragging: false,
//         focusedBlockId: null,
//         canvasScale: 1,

//         setCanvasScale: (scale) => set(() => ({ canvasScale: scale })),
//         // Page actions
//         setActivePage: (id) => {
//           set({ activePageId: id, activeBlockId: null });
//         },
//         createNewPage: () => {
//           const newPage: CanvasPage = {
//             id: crypto.randomUUID(),
//             name: 'New Page',
//             blocks: [],
//             canvasBackgroundColor: get().canvasBackgroundColor,
//             canvasWidth: get().canvasWidth,
//             canvasHeight: get().canvasHeight,
//           };
//           set((state) => ({
//             pages: [...state.pages, newPage],
//             activePageId: newPage.id,
//             activeBlockId: null,
//           }));
//         },
//         deletePage: (id) => {
//           set((state) => {
//             const pages = state.pages.filter((p) => p.id !== id);
//             const newActive =
//               state.activePageId === id
//                 ? pages[0]?.id ?? null
//                 : state.activePageId;
//             return { pages, activePageId: newActive, activeBlockId: null };
//           });
//         },

//         // Block actions (per active page)
//         setActiveBlock: (id) => set({ activeBlockId: id }),

//         updateBlock: (id, updates) =>
//           set((state) => ({
//             pages: state.pages.map((page) =>
//               page.id !== state.activePageId
//                 ? page
//                 : {
//                     ...page,
//                     blocks: page.blocks.map((block) =>
//                       block.id === id ? { ...block, ...updates } : block
//                     ),
//                   }
//             ),
//           })),

//         createNewBlock: async (block) =>
//           set((state) => ({
//             pages: state.pages.map((p) => {
//               if (p.id !== state.activePageId) return p;
//               const highestZ = Math.max(
//                 10,
//                 ...p.blocks.map((b) => b.zIndex ?? 0)
//               );
//               const newBlock: Block = {
//                 ...block,
//                 zIndex: highestZ + 1,
//               } as Block;
//               return { ...p, blocks: [...p.blocks, newBlock] };
//             }),
//             activeBlockId: (() => {
//               const page = state.pages.find((p) => p.id === state.activePageId);
//               return page?.blocks.slice(-1)[0]?.id ?? null;
//             })(),
//           })),

//         deleteBlock: (id) =>
//           set((state) => ({
//             pages: state.pages.map((p) =>
//               p.id !== state.activePageId
//                 ? p
//                 : { ...p, blocks: p.blocks.filter((b) => b.id !== id) }
//             ),
//             activeBlockId:
//               state.activeBlockId === id ? null : state.activeBlockId,
//           })),

//         alignBlock: (position) => {
//           const state = get(); // get current state outside

//           set((state) => {
//             return {
//               pages: state.pages.map((p) => {
//                 if (p.id !== state.activePageId) return p;
//                 const blk = p.blocks.find((b) => b.id === state.activeBlockId);
//                 if (!blk) return p;

//                 const { translate } = blk.transform;
//                 let x = translate.x;
//                 let y = translate.y;

//                 switch (position) {
//                   case 'left':
//                     x = 0;
//                     break;
//                   case 'right':
//                     x = p.canvasWidth - blk.width;
//                     break;
//                   case 'top':
//                     y = 0;
//                     break;
//                   case 'bottom':
//                     y = p.canvasHeight - blk.height;
//                     break;
//                   case 'horizontal-centered':
//                     x = (p.canvasWidth - blk.width) / 2;
//                     break;
//                   case 'vertical-centered':
//                     y = (p.canvasHeight - blk.height) / 2;
//                     break;
//                 }

//                 return {
//                   ...p,
//                   blocks: p.blocks.map((b) =>
//                     b.id === blk.id
//                       ? {
//                           ...b,
//                           transform: { ...b.transform, translate: { x, y } },
//                         }
//                       : b
//                   ),
//                 };
//               }),
//             };
//           });

//           // ✅ Defer updateMoveableRect until after DOM has updated
//           requestAnimationFrame(() => {
//             if (state.activeBlockId) {
//               useMoveableStore.getState().updateMoveableRect();
//             }
//           });
//         },

//         reorderBlock: (id, direction) =>
//           set((state) => ({
//             pages: state.pages.map((p) => {
//               if (p.id !== state.activePageId) return p;
//               const blocks = [...p.blocks];
//               const blk = blocks.find((b) => b.id === id);
//               if (!blk) return p;
//               console.log('blk', blk);
//               const MIN_Z = 10;
//               // maintain sorted list for accurate reordering
//               const sorted = [...blocks].sort(
//                 (a, b) => (a.zIndex ?? MIN_Z) - (b.zIndex ?? MIN_Z)
//               );
//               const zList = sorted.map((b) => b.zIndex ?? MIN_Z);
//               let targetZ = blk.zIndex ?? MIN_Z;

//               if (direction === 'front') targetZ = Math.max(...zList) + 1;
//               else if (direction === 'back') targetZ = MIN_Z;
//               else if (direction === 'forward') targetZ = targetZ + 1;
//               else if (direction === 'backward') targetZ = targetZ - 1;

//               if (targetZ < MIN_Z) targetZ = MIN_Z;

//               return {
//                 ...p,
//                 blocks: blocks.map((b) => {
//                   if (b.id === blk.id) return { ...b, zIndex: targetZ };
//                   if (b.zIndex !== undefined && b.zIndex >= targetZ)
//                     return { ...b, zIndex: b.zIndex + 1 };
//                   return b;
//                 }),
//               };
//             }),
//           })),

//         deSelectBlocks: () =>
//           set((state) => {
//             useMoveableStore.setState({ targets: [] });
//             return {
//               activeBlockId: null,
//             };
//           }),

//         copyBlock: () =>
//           set((state) => ({
//             clipboard:
//               state.pages
//                 .find((p) => p.id === state.activePageId)
//                 ?.blocks.find((b) => b.id === state.activeBlockId) ?? null,
//           })),

//         cutBlock: () =>
//           set((state) => {
//             const page = state.pages.find((p) => p.id === state.activePageId);
//             const block = page?.blocks.find(
//               (b) => b.id === state.activeBlockId
//             );
//             if (!block) return state;
//             return {
//               clipboard: block,
//               pages: state.pages.map((p) =>
//                 p.id !== state.activePageId
//                   ? p
//                   : { ...p, blocks: p.blocks.filter((b) => b.id !== block.id) }
//               ),
//               activeBlockId: null,
//             };
//           }),

//         pasteBlock: () =>
//           set((state) => {
//             const clip = state.clipboard;
//             if (!clip) return state;
//             return {
//               pages: state.pages.map((p) => {
//                 if (p.id !== state.activePageId) return p;
//                 const highestZ = Math.max(
//                   10,
//                   ...p.blocks.map((b) => b.zIndex ?? 0)
//                 );
//                 const newB: Block = {
//                   ...clip,
//                   id: crypto.randomUUID(),
//                   zIndex: highestZ + 1,
//                   transform: {
//                     ...clip.transform,
//                     translate: {
//                       x: clip.transform.translate.x + 10,
//                       y: clip.transform.translate.y + 10,
//                     },
//                   },
//                 };
//                 return { ...p, blocks: [...p.blocks, newB] };
//               }),
//               activeBlockId: clip.id,
//             };
//           }),

//         duplicateBlock: () => {
//           set((state) => {
//             const { activeBlockId, activePageId, pages } = state;
//             if (!activeBlockId || !activePageId) return state;

//             const pageIndex = pages.findIndex((p) => p.id === activePageId);
//             if (pageIndex === -1) return state;

//             const page = pages[pageIndex];
//             const block = page.blocks.find((b) => b.id === activeBlockId);
//             if (!block) return state;

//             const newBlock = {
//               ...structuredClone(block),
//               id: `${crypto.randomUUID()}`,
//               zIndex: Math.max(...page.blocks.map((b) => b.zIndex ?? 0)) + 1,
//               transform: {
//                 ...block.transform,
//                 translate: {
//                   x: block.transform.translate.x + 20,
//                   y: block.transform.translate.y + 20,
//                 },
//               },
//             };

//             const updatedPage = {
//               ...page,
//               blocks: [...page.blocks, newBlock],
//             };

//             const updatedPages = [...pages];
//             updatedPages[pageIndex] = updatedPage;

//             return {
//               ...state,
//               pages: updatedPages,
//               activeBlockId: newBlock.id,
//               selectedBlockIds: [newBlock.id],
//             };
//           });
//         },

//         setCanvasBackgroundColor: (color) =>
//           set((state) => ({
//             canvasBackgroundColor: color,
//             pages: state.pages.map((p) =>
//               p.id === state.activePageId
//                 ? { ...p, canvasBackgroundColor: color }
//                 : p
//             ),
//           })),

//         setCanvasSize: (w, h) =>
//           set((state) => ({
//             canvasWidth: w,
//             canvasHeight: h,
//             pages: state.pages.map((p) =>
//               p.id === state.activePageId
//                 ? { ...p, canvasWidth: w, canvasHeight: h }
//                 : p
//             ),
//           })),

//         resetPage: () =>
//           set((state) => ({
//             pages: state.pages.map((p) =>
//               p.id !== state.activePageId
//                 ? p
//                 : {
//                     ...p,
//                     blocks: p.blocks.map((b, i) => ({ ...b, zIndex: i + 10 })),
//                   }
//             ),
//           })),

//         setIsDragging: (isDragging) => set({ isDragging }),
//         setFocusedBlockId: (id) => set({ focusedBlockId: id }),
//       };
//     },
//     { name: 'canvas-store' }
//   )
// );

// export const useBlocks = () =>
//   useCanvasStore(
//     (state) => state.pages.find((p) => p.id === state.activePageId)?.blocks
//   );

// addShape: (shape: Shapes) => {
//   const { activePageId, pages, canvasWidth, canvasHeight } = get();
//   if (!activePageId) return;

//   const activePage = pages.find((p) => p.id === activePageId);
//   if (!activePage) return;

//   const highestZ = Math.max(
//     10,
//     ...activePage.blocks.map((b) => b.zIndex ?? 0)
//   );

//   const shapeBlock: ShapeBlockInterface = {
//     id: crypto.randomUUID(),
//     type: 'shape',
//     shape: shape,
//     width: 100,
//     height: 100,
//     transform: {
//       translate: {
//         x: (canvasWidth - 100) / 2,
//         y: (canvasHeight - 100) / 2,
//       },
//       rotate: 0,
//       flipHorizontal: 1,
//       flipVertical: 1,
//     },
//     zIndex: highestZ + 1,

//     // ✅ Add the missing required fields
//     src: '', // Hexagons might not use an image, so use an empty string or a default placeholder
//     title: '',
//   };

//   set((state) => ({
//     pages: state.pages.map((p) =>
//       p.id === activePageId
//         ? { ...p, blocks: [...p.blocks, shapeBlock] }
//         : p
//     ),
//     activeBlockId: shapeBlock.id,
//   }));
// },
